import { AmplifyLogo, ApiStatus } from '@/components/common';
import { useAuth } from '@/services/auth';
import { amplifyTheme } from '@/theme/amplifyTheme';
import {
    Avatar,
    Button,
    FluentProvider,
    makeStyles,
    Menu,
    MenuDivider,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    shorthands,
    Text,
    tokens,
    webLightTheme,
} from '@fluentui/react-components';
import {
    DataUsage20Regular,
    Flow20Regular,
    Home20Regular,
    Navigation20Regular,
    Play20Regular,
    PlugConnected20Regular,
    Settings20Regular,
    SignOut20Regular,
} from '@fluentui/react-icons';
import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: amplifyTheme.lightGray,
  },
  header: {
    backgroundColor: amplifyTheme.lightBlue,
    ...shorthands.padding('16px', '24px'),
    boxShadow: '0 2px 8px rgba(0, 102, 204, 0.1)',
    position: 'relative',
    zIndex: 1001,
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('16px'),
  },
  main: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  nav: {
    width: '260px',
    backgroundColor: 'white',
    ...shorthands.padding('24px', '16px'),
    boxShadow: '2px 0 8px rgba(0, 0, 0, 0.05)',
    '@media (max-width: 768px)': {
      position: 'absolute',
      top: '68px',
      left: 0,
      bottom: 0,
      zIndex: 1000,
      transform: 'translateX(-100%)',
      transition: 'transform 0.3s ease',
    },
  },
  navOpen: {
    '@media (max-width: 768px)': {
      transform: 'translateX(0)',
    },
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderRadius('8px'),
    textDecoration: 'none',
    color: amplifyTheme.textSecondary,
    marginBottom: '8px',
    transition: 'all 0.2s ease',
    fontWeight: '500',
    '&:hover': {
      backgroundColor: amplifyTheme.lightBlue,
      color: amplifyTheme.primaryBlue,
      transform: 'translateX(4px)',
    },
    '&.active': {
      background: amplifyTheme.gradient,
      color: 'white',
      fontWeight: '600',
      boxShadow: amplifyTheme.shadowMd,
    },
  },
  navIcon: {
    fontSize: '20px',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: amplifyTheme.lightGray,
  },
  userMenu: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('16px'),
  },
  mobileMenuButton: {
    display: 'none',
    color: amplifyTheme.primaryBlue,
    '@media (max-width: 768px)': {
      display: 'flex',
    },
  },
  overlay: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'block',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999,
    },
  },
  themeButton: {
    color: amplifyTheme.primaryBlue,
    ':hover': {
      backgroundColor: 'rgba(0, 102, 204, 0.1)',
      ...shorthands.borderRadius('4px'),
    },
  },
  userButton: {
    color: amplifyTheme.darkBlue,
    fontWeight: '600',
    ':hover': {
      backgroundColor: 'rgba(0, 102, 204, 0.1)',
      ...shorthands.borderRadius('4px'),
    },
  },
  avatar: {
    backgroundColor: amplifyTheme.primaryOrange,
    color: 'white',
  },
});

const navItems = [
  { key: 'dashboard', label: 'Dashboard', icon: <Home20Regular />, path: '/' },
  {
    key: 'connectors',
    label: 'Connectors',
    icon: <PlugConnected20Regular />,
    path: '/connectors',
  },
  { key: 'runs', label: 'Runs', icon: <Play20Regular />, path: '/runs' },
  {
    key: 'pipeline-flow',
    label: 'Pipeline Flow',
    icon: <Flow20Regular />,
    path: '/pipeline-flow',
  },
  {
    key: 'queues',
    label: 'Queues',
    icon: <DataUsage20Regular />,
    path: '/queues',
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: <Settings20Regular />,
    path: '/settings',
  },
];

export const Layout: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <div className={styles.root}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: tokens.spacingHorizontalM,
              }}
            >
              <Button
                appearance="subtle"
                icon={<Navigation20Regular />}
                className={styles.mobileMenuButton}
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                aria-label="Menu"
              />
              <AmplifyLogo height={60} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <ApiStatus />
              <div className={styles.userMenu}>
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <Button
                    appearance="subtle"
                    icon={
                      <Avatar name={user?.name} className={styles.avatar} />
                    }
                    iconPosition="before"
                    className={styles.userButton}
                  >
                    {user?.name}
                  </Button>
                </MenuTrigger>

                <MenuPopover>
                  <MenuList>
                    <MenuItem disabled>
                      <div>
                        <Text weight="semibold">{user?.name}</Text>
                        <br />
                        <Text size={200}>{user?.email}</Text>
                      </div>
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem
                      icon={<SignOut20Regular />}
                      onClick={handleLogout}
                    >
                      Logout
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
              </div>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <div className={styles.main}>
          {/* Mobile nav overlay */}
          {mobileNavOpen && (
            <div
              className={styles.overlay}
              onClick={() => setMobileNavOpen(false)}
            />
          )}

          {/* Navigation */}
          <nav
            className={`${styles.nav} ${mobileNavOpen ? styles.navOpen : ''}`}
            role="navigation"
          >
            {navItems.map(item => (
              <NavLink
                key={item.key}
                to={item.path}
                className={({ isActive }) =>
                  `${styles.navItem} ${isActive ? 'active' : ''}`
                }
                onClick={() => setMobileNavOpen(false)}
              >
                <span className={styles.navIcon}>{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Page content */}
          <main className={styles.content}>
            <Outlet />
          </main>
        </div>
      </div>
    </FluentProvider>
  );
};
